import { Fragment, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, IconButton } from '@upmc/ims.component-library';

import * as ancillaryActions from '../../../../store/ancillary/actions';
import * as appsActions from '../../../../store/apps/actions';
import Icon from '../../MDC/Icon/Icon';
import propTypes from 'prop-types';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classnames from 'classnames';
import { CSS_CLASSES } from './_constants';
import msalConfig from 'services/msal/config';
import { acquireAccessToken } from 'services/msal/_utilities';

import './PopupIconButton.scss';

const PopupIconButton = props => {
	/**
	 * Checks if @param obj contains property with name @param prop
	 * @param {Object} obj object to check in
	 * @param {String} prop property name to check for
	 * @returns {Object|String} nullable value of property in object
	 * @example
	 * // returns 'info' if obj of structure {PopupIconButton: {iconType: 'info'...}...}
	 * getProp(obj.PopupIconButton, 'iconType')
	 */
	const getProp = (obj, prop) => {
		if (obj !== null) {
			if (obj.hasOwnProperty(prop)) {
				return obj[prop];
			}
		}

		return null;
	};

	const dispatch = useDispatch();
	const [sSessionValue, setSessionValue] = useState(null);
	const refIntervalId = useRef();
	const allForms = useSelector(state => state.apps.allForms);
	const formIndex = Object.keys(allForms).find(
		formIndex => allForms[formIndex].id === props.appId
	);
	const model = useSelector(state => state.apps.allForms[formIndex].model);

	// default props
	const objIcon = getProp(props, 'popupiconbutton');
	const isIconButton =
		objIcon !== null && getProp(objIcon, 'isIconButton') !== null
			? objIcon.isIconButton
			: true;
	const iconType = objIcon !== null ? getProp(objIcon, 'icon') : null;
	const hoverText = objIcon !== null ? getProp(objIcon, 'hoverText') : null;
	const objAction = objIcon !== null ? getProp(objIcon, 'action') : null;
	const objActionUrl = objAction !== null ? getProp(objAction, 'url') : null;
	const actionUrlPath =
		objActionUrl !== null ? getProp(objActionUrl, 'path') : null;
	const actionUrlValue = props.value ? props.value : null;
	const arrActionUrlPath =
		actionUrlPath !== null && actionUrlValue !== null
			? actionUrlPath.split('$value')
			: null;
	const url =
		arrActionUrlPath !== null
			? arrActionUrlPath[0] + actionUrlValue + arrActionUrlPath[1]
			: actionUrlPath;
	const actionWindowName =
		objAction !== null ? getProp(objAction, 'windowName') : null;
	const windowFeatures =
		objAction !== null
			? String(getProp(objAction, 'windowFeatures')).split(', ')
			: null;
	const label = objIcon !== null ? getProp(objIcon, 'label') : null;

	// session props
	const objSession = objIcon !== null ? getProp(objIcon, 'session') : null;
	const sessionIntervalDelay =
		objSession !== null ? getProp(objSession, 'intervalDelay') : 2000;
	const sessionKey = objSession !== null ? getProp(objSession, 'key') : null;
	const objSessionChangeAction =
		objSession !== null ? getProp(objSession, 'onChangeAction') : null;
	const sessionChangeActionRoute =
		objSessionChangeAction !== null
			? getProp(objSessionChangeAction, 'route')
			: null;
	const sessionChangeActionTargetComponentKey =
		objSessionChangeAction !== null
			? getProp(objSessionChangeAction, 'targetComponentKey')
			: null;
	const sessionChangeActionResetSession =
		objSessionChangeAction !== null
			? getProp(objSessionChangeAction, 'resetSession')
			: null;

	const setSession = async key => {
		const token = await acquireAccessToken(msalConfig.requests.api);
		const response = await fetch('../Session/GetSessionValue/', {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ key: key }),
		});
		let data = await response.json();
		data = data.length > 0 ? data : null;
		if (data !== sSessionValue) {
			setSessionValue(data);
			if (
				refIntervalId.current !== undefined &&
				refIntervalId.current !== 0
			) {
				// if active interval
				clearInterval(refIntervalId.current);
				const targetComponentKey =
					sessionChangeActionTargetComponentKey !== null
						? sessionChangeActionTargetComponentKey
						: props.name;
				if (objSessionChangeAction !== null) {
					const payload = {
						formIndex: formIndex,
						appId: props.appId,
						model: model,
						targetComponentKey: targetComponentKey,
						sessionValue: data,
						route: sessionChangeActionRoute,
					};
					dispatch(ancillaryActions.PopupSessionChange(payload));
					if (
						model.members[targetComponentKey].validation
							.validationRoute !== null &&
						!model.members[targetComponentKey].submitted
					) {
						dispatch(
							appsActions.callApiValidate({
								memberToValidateKey: targetComponentKey,
								formToValidateKey: formIndex,
								allMembers: model.members,
								componentMap: model.componentMap,
								trackingNumber: model.TrackingNumber,
							})
						);
					}
				}
			}
			if (sessionChangeActionResetSession) {
				const token = await acquireAccessToken(msalConfig.requests.api);
				// if configured to reset session on change
				fetch('../Session/SetSessionValue/', {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ key: key, value: null }),
				});
				setSessionValue(null);
			}
		}
	};

	useEffect(() => {
		if (sessionKey !== null) {
			setSession(sessionKey); // initialize state with existing session value
			return () => {
				// clear hanging interval prior to dismount
				clearInterval(refIntervalId.current);
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const localProps = {
		'aria-label': hoverText,
		title: hoverText,
		onClick: () => {
			window.open(url, actionWindowName, windowFeatures);
			if (sessionKey !== null) {
				refIntervalId.current = setInterval(() => {
					setSession(sessionKey);
				}, sessionIntervalDelay);
			}
		},
	};
	const icon = <Icon icon={iconType} />;
	let iconButton = isIconButton ? (
		<IconButton {...props} {...localProps}>
			{icon}
		</IconButton>
	) : null;
	const popupIconButton =
		iconButton !== null ? (
			label !== null ? (
				<Fragment>
					{iconButton}
					{label}
				</Fragment>
			) : (
				iconButton
			)
		) : (
			<Button
				{...props}
				{...localProps}
				startIcon={icon}
				variant='outlined'
			>
				{label}
			</Button>
		);

	return (
		<StyleAuxiliary
			classes={classnames({
				[CSS_CLASSES.ROOT]: true,
				[`mdc-form-field`]: true,
			})}
			{...props}
		>
			{popupIconButton}
		</StyleAuxiliary>
	);
};

PopupIconButton.propTypes = {
	action: propTypes.object,
	hoverText: propTypes.string,
	icon: propTypes.string,
	isIconButton: propTypes.bool,
	intervalDelay: propTypes.number,
	key: propTypes.string,
	label: propTypes.string,
	onChangeAction: propTypes.object,
	path: propTypes.string,
	popupiconbutton: propTypes.object,
	resetSession: propTypes.bool,
	route: propTypes.string,
	session: propTypes.object,
	targetComponentKey: propTypes.string,
	url: propTypes.string,
	value: propTypes.string,
	windowFeatures: propTypes.string,
	windowName: propTypes.string,
};

export default PopupIconButton;
